import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useRecoilState } from 'recoil';
import { OrganizationLocation } from '@/common/types';
import { usePaginationContext } from '@/components/context/hooks/PaginationContext';
import { scanFilterState } from '@/recoil/atoms/scanFilterAtom';
import { MultiSelectFilter } from './MultiSelectFilter';
import { SingleSelectFilter } from './SingleSelectFilter';

interface LocationMultiFilterProps {
    organizationLocations: OrganizationLocation[];
    disabled?: boolean;
}

export const LocationMultiFilter = (props: LocationMultiFilterProps) => {
    const { organizationLocations, disabled } = props;
    const [scanFilters, setScanFilters] = useRecoilState(scanFilterState);
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
    const { page, setPage } = usePaginationContext();

    useEffect(() => {
        if (organizationLocations.length === 0 || selectedOrgId !== null || scanFilters.locationIds.length === 0)
            return;
        const persistedIds = scanFilters.locationIds as string[];
        const match = organizationLocations.find((o) =>
            o.locations.some((l) => persistedIds.includes(l.id.toString())),
        );
        if (match) setSelectedOrgId(match.id.toString());
    }, [organizationLocations, selectedOrgId, scanFilters.locationIds]);

    const orgOptions = organizationLocations.map((o) => ({ id: o.id.toString(), name: o.name }));

    const selectedOrg = selectedOrgId
        ? organizationLocations.find((o) => o.id.toString() === selectedOrgId) ?? null
        : null;

    const locationOptions = selectedOrg
        ? selectedOrg.locations.map((l) => ({ id: l.id.toString(), name: l.name }))
        : [];

    const handleOrgChange = (orgId: string | null) => {
        setSelectedOrgId(orgId);
        if (page !== 1) {
            setPage(1);
        }
        const newLocationIds = orgId
            ? (organizationLocations.find((o) => o.id.toString() === orgId)?.locations ?? []).map((l) =>
                  l.id.toString(),
              )
            : [];
        setScanFilters({ ...scanFilters, locationIds: newLocationIds });
    };

    const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (page !== 1) {
            setPage(1);
        }
        const current = [...scanFilters.locationIds];
        if (event.target.checked) {
            setScanFilters({ ...scanFilters, locationIds: [...current, event.target.name] });
        } else {
            const i = current.indexOf(event.target.name);
            if (i > -1) {
                current.splice(i, 1);
            }
            setScanFilters({ ...scanFilters, locationIds: current });
        }
    };

    const handleClearLocations = () => {
        if (page !== 1) {
            setPage(1);
        }
        setScanFilters({ ...scanFilters, locationIds: [] });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, '& > div, & .MuiButton-root': { width: '100%' } }}>
            <SingleSelectFilter
                options={orgOptions}
                selected={selectedOrgId}
                handleChange={handleOrgChange}
                label="Organizations"
                prefix="Organization"
                disabled={disabled}
            />
            <MultiSelectFilter
                selected={scanFilters.locationIds}
                options={locationOptions}
                handleRadioChange={handleLocationChange}
                handleClearSelection={handleClearLocations}
                label="Locations"
                prefix="Locations"
                disabled={disabled || !selectedOrgId}
            />
        </Box>
    );
};
